import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAdminRole = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles" as any)
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!data);
  };

  useEffect(() => {
    let expiryTimer: ReturnType<typeof setTimeout> | null = null;

    const SESSION_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

    const scheduleExpiry = (session: Session | null) => {
      if (expiryTimer) clearTimeout(expiryTimer);
      if (!session) return;

      const loginTime = localStorage.getItem("admin_login_time");
      const now = Date.now();

      if (!loginTime) {
        localStorage.setItem("admin_login_time", String(now));
      }

      const elapsed = now - Number(localStorage.getItem("admin_login_time") || now);
      const remaining = SESSION_MAX_AGE_MS - elapsed;

      if (remaining <= 0) {
        supabase.auth.signOut();
        localStorage.removeItem("admin_login_time");
        return;
      }

      expiryTimer = setTimeout(() => {
        supabase.auth.signOut();
        localStorage.removeItem("admin_login_time");
      }, remaining);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => checkAdminRole(session.user.id), 0);
          scheduleExpiry(session);
        } else {
          setIsAdmin(false);
          localStorage.removeItem("admin_login_time");
        }
        setIsLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminRole(session.user.id);
        scheduleExpiry(session);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    localStorage.setItem("admin_login_time", String(Date.now()));
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, isLoading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
